import React, { useState } from 'react'
import {
  Create,
  TextInput,
  PasswordInput,
  email,
  useTranslate,
  TabbedForm,
  FormTab,
  useNotify,
  useRedirect,
  required,
  FormDataConsumer,
  TabbedFormTabs,
  SelectInput,
  SaveButton,
  Toolbar,
  useGetIdentity
} from 'react-admin'
import { Grid, Button } from '@mui/material'
import { FieldsByApplication } from 'src/components/pages/authentication/user/logic/FieldsByApplication'
import { CardCommon } from 'src/components/uiElements'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { Add, Delete } from '@mui/icons-material'
import { WrapperToPermission } from 'src/components/pages/authentication/user/logic/WrapperToPermission'
import { WrapperClient } from 'src/components/pages/authentication/user/logic/WrapperClient'
import { WrapperApplication } from 'src/components/pages/authentication/user/logic/WrapperApplication'
import { WrapperDashboard } from 'src/components/pages/authentication/user/logic/WrapperDashboard'
import { WrapperRoles } from 'src/components/pages/authentication/user/logic/WrapperRoles'
import { generateRandomKey } from 'src/helper/generateRandomKey'
import { BooleanInput } from 'src/components/uiElements/fields/BooleanInput'
import { WrapperConfig } from 'src/components/pages/authentication/user/logic/WrapperConfig'
import { LoadingPermissionProvider } from 'src/components/pages/authentication/user/context/LoadingPermissionContext'
import { AutocompleteInput } from 'src/components/uiElements/fields/AutocompleteInput'

export const UserCreate = (props) => {
  const translate = useTranslate()
  const notify = useNotify()
  const redirect = useRedirect()
  const { identity } = useGetIdentity()
  const [arrayInput, setArrayInput] = useState([])
  const [isSuperadmin, setIsSuperadmin] = useState(false)
  const [selectedHashClient, setSelectedHashClient] = useState()
  const timezones = Intl.supportedValuesOf('timeZone').map(tz => ({
      id: tz,
      name: tz,
    }))
  const [filteredOptions, setFilteredOptions] = useState(timezones)

  const addList = () => {
    setArrayInput((prev) => ([
      ...prev,
      {
        id: generateRandomKey(9),
        clients: { optionsClient: [], checkedClient: identity.client_id },
        applications: { optionsApplication: [], checkedApplication: 1 },
        roles: { optionsRole: [], selectedRole: [] },
        permissions: { left: [], right: [] },
        dashboard: null,
        config: {},
      },
    ]))
  }

  const removeList = (id) => {
    setArrayInput((prev) => prev.filter((item) => item.id !== id))
  }

  const listApplications = () => {
    let applications = []
    let listApplications = [...arrayInput]
    for (const application of listApplications) {
      if (Object.keys(application).length !== 0) {
        const configByApp = application.allApplicationToFilter?.find((elem) => (
          elem.application_id === application.applications.checkedApplication
        ))

        applications.push({
          client_id: application.clients.checkedClient,
          application_id: application.applications.checkedApplication,
          role_id: application.roles.selectedRole,
          permission_id: application.permissions.right.map((item) => item.id),
          endpoint: configByApp?.config?.reactAdmin?.components?.user?.create?.endpoint,
          field_application: application.field_application,
          dashboard: application?.dashboard,
          config: application?.config,
        })
      }
    }
    return applications
  }

  const onFilterOptions = (value) =>{
    const newValue = value.target?.value
    setTimeout(() => {
      if(newValue){
        setFilteredOptions(timezones.filter(timezone=>timezone.toLocaleLowerCase()?.includes(newValue)))
      } else {
        setFilteredOptions(timezones)
      }
    }, 1000);
  }

  const transform = (data) => ({
    firstname: data.firstname,
    mail: data.mail,
    password: data.password,
    surname: data.surname,
    username: data.username,
    language: data.language,
    is_staff: data.is_staff || true,
    is_admin: data.is_admin || false,
    is_superadmin: data.is_superadmin || false,
    timezone: data.timezone,
    applications: listApplications(),
  })

  const onSuccess = () => {
    notify('ra.notification.created_success_masculine', {
      type: 'success',
      messageArgs: { element: translate('ra.single_entity.user') },
    })
    redirect('/user')
  }

  const onError = (error) => {
    if(error?.status === 418){
      notify(error?.body?.errors[0], {
        type: 'error',
      })
    } else {
      notify('ra.notification.created_error_masculine', {
        type: 'error',
        messageArgs: { element: translate('ra.single_entity.user') },
      })
    }
  }

  const noSpaces = (value) =>
    value && /\s/.test(value) ? 'ra.validation.noSpaces' : undefined;

  return (
    <Create {...props} transform={transform} mutationOptions={{ onSuccess, onError }} actions={null}>
      <CardCommon customTitle={translate('ra.title.create_user')}>
        <TabbedForm
          tabs={<TabbedFormTabs variant='scrollable' scrollButtons='auto' />}
          toolbar={
            <Toolbar>
              <SaveButton
                label={translate('ra.action.save')}
                disabled={arrayInput.length === 0}
              />
            </Toolbar>
          }
        >
          <FormTab label={translate('ra.entity.user')}>
            <Grid container spacing={3}>
              <EditInputsWrap label={translate('ra.field.firstname')} required>
                <TextInput source='firstname' label='' validate={[required()]} fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.surname')} required>
                <TextInput source='surname' label='' validate={[required()]} fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.username')} required>
                <TextInput source='username' label='' validate={[required(), (val)=>noSpaces(val)]} fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.email')} required>
                <TextInput
                  type='email'
                  source='mail'
                  label=''
                  fullWidth
                  validate={[required(), (val)=>noSpaces(val), email('invalid email')]}
                />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.password')} required>
                <PasswordInput source='password' label='' validate={[required()]} fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.language')} required>
                <SelectInput
                  source='language'
                  optionText='name'
                  optionValue='key'
                  fullWidth
                  choices={[
                    { name: translate('ra.language.spanish'), key: 'es-AR' },
                    { name: translate('ra.language.english'), key: 'en-US' },
                    { name: translate('ra.language.pt_br'), key: 'pt-BR' },
                  ]}
                  label=''
                  validate={[required()]}
                />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.timezone')} required>
                <AutocompleteInput
                  source='timezone'
                  options={
                    filteredOptions
                  }
                  id='timezone'
                  size='small'
                  optionsText={['name']}
                  label={false}
                  optionValue='id'
                  useOptionValue
                  onSearch={onFilterOptions}
                  resetOnLoad={false}
                  fields={['name']}
                  validate={required()}
                  disablePortal={false}
                />
              </EditInputsWrap>
              {(identity?.is_superadmin || identity?.is_admin) && (
                <EditInputsWrap label={translate('ra.field.is_admin')}>
                  <BooleanInput
                    label={translate('ra.field.is_admin')}
                    source='is_admin'
                    defaultValue={false}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        setIsSuperadmin(false)
                      }
                    }}
                  />
                </EditInputsWrap>
              )}
              {identity?.is_superadmin && (
                <>
                  <EditInputsWrap label={translate('ra.field.is_staff')}>
                    <BooleanInput
                      label={translate('ra.field.is_staff')}
                      source='is_staff'
                      defaultValue={true}
                      onChange={(e) => {
                        if (!e.target.checked) {
                          setIsSuperadmin(false)
                        }
                      }}
                    />
                  </EditInputsWrap>
                  <FormDataConsumer>
                    {({ formData }) => (
                      <EditInputsWrap label={translate('ra.field.is_superadmin')}>
                        <BooleanInput
                          label={translate('ra.field.is_superadmin')}
                          source='is_superadmin'
                          disabled={!formData.is_staff}
                          value={isSuperadmin}
                          defaultValue={false}
                          onChange={(e) => setIsSuperadmin(e.target.checked)}
                        />
                      </EditInputsWrap>
                    )}
                  </FormDataConsumer>
                </>
              )}
            </Grid>
          </FormTab>
          <FormTab label={translate('ra.entity.config_application')}>
            {arrayInput.map((item, i) => (
              <LoadingPermissionProvider key={i}>
                {Object.keys(item).length !== 0 ? (
                  <>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={4} className='uiInputLabel'>
                        <WrapperClient setSelectedHashClient={setSelectedHashClient} index={i} setList={setArrayInput} item={item} id={item.id} />
                        <WrapperApplication
                          index={i}
                          setList={setArrayInput}
                          item={item}
                          id={item.id}
                        />
                        <EditInputsWrap label={translate('ra.field.dashboard')} md={12}>
                          <WrapperDashboard index={i} setList={setArrayInput} item={item} id={item.id} />
                        </EditInputsWrap>
                        <EditInputsWrap
                          label={translate('ra.field.json')}
                          md={12}
                          style={{ paddingBottom: '20px' }}
                        >
                          <WrapperConfig index={i} setList={setArrayInput} item={item} id={item.id} />
                        </EditInputsWrap>
                        <EditInputsWrap label={translate('ra.field.role')} md={12}>
                          <WrapperRoles index={i} setList={setArrayInput} item={item} id={item.id} />
                        </EditInputsWrap>
                        <FieldsByApplication selectedHashClient={selectedHashClient} index={i} setList={setArrayInput} item={item} id={item.id} />
                      </Grid>
                      <Grid item xs={12} sm={8} className='uiInputLabel'>
                        <EditInputsWrap label={translate('ra.field.permission')} md={12}>
                          <WrapperToPermission setList={setArrayInput} item={item} id={item.id} />
                        </EditInputsWrap>
                      </Grid>
                    </Grid>
                    <Grid container spacing={3} justifyContent='flex-end' className='addItemSeparator'>
                      <Grid item xs={12} sm={12} className='grid'>
                        <Button onClick={() => removeList(item.id)} startIcon={<Delete />} color='error'>
                          {translate('ra.action.delete')}
                        </Button>
                      </Grid>
                    </Grid>
                  </>
                ) : null}
              </LoadingPermissionProvider>
            ))}
            <Button startIcon={<Add />} onClick={addList}>
              {translate('ra.action.add_specific', {
                name: translate('ra.field.applications'),
              })}
            </Button>
          </FormTab>
        </TabbedForm>
      </CardCommon>
    </Create>
  )
}
